import { supabase } from '../supabase';
import { isPathBasedRoutingHost } from './subdomainRouter';
import { CUSTOM_THEME_ID, isCustomThemeId, THEME_CATALOG } from '../../../themes/registry';

export const PRODUCTION_CUSTOMER_APP_URL = 'https://app.table-tap.in';
export const STAGING_CUSTOMER_APP_URL = 'https://staging.app.table-tap.in';
/** Vercel dev branch — path routing like localhost (/restaurant, /admin, / for customer). */
export const DEV_VERCEL_CUSTOMER_APP_URL = 'https://table-tap-app.vercel.app';

/**
 * Base URL for the customer (QR) app — used in admin-generated preview links.
 *
 * Priority: path-based dev host (localhost / vercel.app) → REACT_APP_CUSTOMER_APP_URL →
 * staging.table-tap.in → production table-tap.in.
 *
 * Localhost wins over env so Doppler/staging .env files do not send preview links to production.
 */
export function getCustomerAppBaseUrl() {
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    const host = hostname.toLowerCase();

    if (isPathBasedRoutingHost(host)) {
      return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    }

    if (host.startsWith('staging.') && host.endsWith('table-tap.in')) {
      return STAGING_CUSTOMER_APP_URL;
    }

    if (host.includes('table-tap.in')) {
      return PRODUCTION_CUSTOMER_APP_URL;
    }
  }

  const fromEnv = process.env.REACT_APP_CUSTOMER_APP_URL;
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }

  return PRODUCTION_CUSTOMER_APP_URL;
}

/**
 * Table QR link for guests — always uses the customer app host, not the restaurant portal.
 * @param {string} restaurantId
 * @param {string|number} tableNumber
 */
export function buildTableQrUrl(restaurantId, tableNumber) {
  if (!restaurantId || tableNumber == null || tableNumber === '') return '';

  const params = new URLSearchParams({
    restaurant: restaurantId,
    table: String(tableNumber),
  });

  return `${getCustomerAppBaseUrl()}/?${params.toString()}`;
}

/**
 * Preview link — loads restaurant QR context but overrides theme via ?previewTheme=
 * @param {string} restaurantId
 * @param {string} previewThemeId — custom theme uuid or platform id (a, b)
 * @param {string|number} [tableNumber='1']
 */
export function buildThemePreviewUrl(restaurantId, previewThemeId, tableNumber = '1') {
  if (!restaurantId || !previewThemeId) return '';

  const params = new URLSearchParams({
    restaurant: restaurantId,
    table: String(tableNumber),
    previewTheme: previewThemeId,
  });

  return `${getCustomerAppBaseUrl()}/?${params.toString()}`;
}

/** Read previewTheme from current page URL (customer app). */
export function getPreviewThemeFromUrl(
  search = typeof window !== 'undefined' ? window.location.search : '',
) {
  if (!search) return null;
  return new URLSearchParams(search).get('previewTheme');
}

/**
 * Resolve ?previewTheme= to runtime theme fields (does not change DB).
 * @returns {Promise<{ themeId, themeType, customThemeSlug } | null>}
 */
export async function resolveThemePreview(restaurantId, previewThemeParam) {
  if (!restaurantId || !previewThemeParam) return null;

  const param = previewThemeParam.trim();
  const platform = THEME_CATALOG.find((t) => t.id === param);
  if (platform) {
    return { themeId: platform.id, themeType: 'platform', customThemeSlug: null };
  }

  if (isCustomThemeId(param) && param !== CUSTOM_THEME_ID) {
    const { data } = await supabase
      .from('restaurant_custom_themes')
      .select('id, slug, restaurant_id, status')
      .eq('restaurant_id', restaurantId)
      .eq('id', param)
      .maybeSingle();

    if (data) {
      return {
        themeId: data.id,
        themeType: 'custom',
        customThemeSlug: data.slug || null,
      };
    }
  }

  const { data: bySlug } = await supabase
    .from('restaurant_custom_themes')
    .select('id, slug, status')
    .eq('restaurant_id', restaurantId)
    .eq('slug', param)
    .maybeSingle();

  if (bySlug) {
    return {
      themeId: bySlug.id,
      themeType: 'custom',
      customThemeSlug: bySlug.slug || null,
    };
  }

  return null;
}
